import { useEffect, useState } from 'react'
import { ArrowRight, Loader2 } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog'
import { Separator } from '@/shared/ui/separator'
import { t } from '@/shared/lib/i18n'
import apiClient from '@/shared/lib/apiClient'
import { instrumentReportsStatusChangesByDatePath } from '@/shared/lib/routes'
import { useSiteDateTime } from '@/shared/hooks/useSiteDateTime'

interface StatusChange {
  id: number
  oldStatusLabel: string | null
  newStatusLabel: string | null
  changedByName: string | null
  completedAt: string
  comments: string | null
}

interface Props {
  instrumentId: number
  date: string | null
  onClose: () => void
}

type LoadState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'loaded'; records: StatusChange[] }

const InstrumentStatusChangesDialog = ({ instrumentId, date, onClose }: Props) => {
  const { formatDateTime } = useSiteDateTime()
  const [loadState, setLoadState] = useState<LoadState>({ status: 'idle' })

  const fetchRecords = (targetDate: string) => {
    setLoadState({ status: 'loading' })
    apiClient
      .get<{ statusChanges: StatusChange[] }>(
        instrumentReportsStatusChangesByDatePath(instrumentId, { date: targetDate }),
      )
      .then((res) => setLoadState({ status: 'loaded', records: res.data.statusChanges }))
      .catch(() => setLoadState({ status: 'error' }))
  }

  useEffect(() => {
    if (date) fetchRecords(date)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instrumentId, date])

  const isOpen = date != null

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[80vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('instruments.reports.statusChanges.dialogTitle')}</DialogTitle>
          {date && <p className="text-sm text-muted-foreground">{date}</p>}
        </DialogHeader>

        <Separator />

        {loadState.status === 'loading' && (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {loadState.status === 'error' && (
          <p className="py-4 text-center text-sm text-destructive">
            {t('instruments.reports.logDetails.error')}
          </p>
        )}

        {loadState.status === 'loaded' && loadState.records.length === 0 && (
          <p className="py-4 text-center text-sm text-muted-foreground">
            {t('instruments.reports.dialogs.empty')}
          </p>
        )}

        {loadState.status === 'loaded' && loadState.records.length > 0 && (
          <div className="space-y-3">
            {loadState.records.map((change) => (
              <div key={change.id} className="rounded border p-3 text-sm space-y-1">
                <div className="flex items-center gap-2 text-xs font-medium">
                  <span className="text-muted-foreground">{change.oldStatusLabel ?? '—'}</span>
                  <ArrowRight className="h-3 w-3 text-muted-foreground" />
                  <span>{change.newStatusLabel ?? '—'}</span>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{formatDateTime(change.completedAt)}</span>
                  {change.changedByName && <span>{change.changedByName}</span>}
                </div>
                {change.comments && <p className="text-xs">{change.comments}</p>}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default InstrumentStatusChangesDialog
